import { useState, useCallback, memo, useEffect } from 'react';
import { useSidebar } from '@/context/SidebarContext';
import { EarlyWarningSidebar } from '@/components/shared/Sidebar/EarlyWarningSidebar';
import { Container } from '@/components/Layout/Container';
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';
import { EarlyWarningHeader } from '@/components/Layout/Header/EarlyWarningHeader';
import { useEarlyWarning } from '@/context/EarlyWarningContext';
import { cn } from '@/lib/utils';
import { Plus, AlertTriangle, X } from 'lucide-react';
import { EarlyWarningData } from '@/types/early-warning';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface MainContentProps {
  isCollapsed: boolean;
  projectName: string;
  setProjectName: (value: string) => void;
  clientName: string;
  setClientName: (value: string) => void;
  projectStartDate: string;
  setProjectStartDate: (value: string) => void;
  projectEndDate: string;
  setProjectEndDate: (value: string) => void;
  onAddWarning: () => void;
  handleSort: (field: string) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  filteredData: any[];
  handleEditWarning: (warning: any) => void;
  downloadCSV: () => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  warningData: any[];
}

const MemoizedPrimaryHeader = memo(PrimaryHeader);
const MemoizedEarlyWarningHeader = memo(EarlyWarningHeader);

const ContentSection = memo(
  ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border-b pb-4 last:border-b-0 last:pb-0 last:pt-4">
      <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-4">{title}</h2>
      {children}
    </div>
  )
);

const DataVisualizations = ({ filteredData, warningData }) => {
  const statusCounts = {
    'ACTIVE': warningData.filter(w => w.status === 'ACTIVE').length,
    'OVERDUE': warningData.filter(w => w.status === 'OVERDUE').length,
    'AVOIDED': warningData.filter(w => w.status === 'AVOIDED').length,
  };

  const statusData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  const statusColors = {
    'ACTIVE': '#3B82F6',
    'OVERDUE': '#EF4444',
    'AVOIDED': '#10B981',
  };

  const impactTypes = [
    { id: 'increasePrices', name: 'Increase Prices' },
    { id: 'delayCompletion', name: 'Delay Completion' },
    { id: 'delayKeyDate', name: 'Delay Key Date' },
    { id: 'impairPerformance', name: 'Impair Performance' },
    { id: 'increaseContractorCost', name: 'Increase Cost' },
  ];

  const impactData = impactTypes.map(impact => {
    const count = warningData.filter(w => w.impacts?.[impact.id]).length;
    const percentage = warningData.length > 0 ? Math.round((count / warningData.length) * 100) : 0;
    return { name: impact.name, count, percentage };
  });

  const riskMatrixData = [];
  for (let severity = 1; severity <= 5; severity++) {
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      const score = severity * likelihood;
      const count = warningData.filter(w => 
        w.severity === severity && w.likelihood === likelihood
      ).length;
      let color = "#F3F4F6";
      if (score >= 15) color = "#FEE2E2";
      else if (score >= 10) color = "#FEF3C7";
      else if (score >= 6) color = "#FEFCE8";
      riskMatrixData.push({ severity, likelihood, score, count, color });
    }
  }

  const warningsByMonth = warningData.reduce((acc, warning) => {
    const date = warning.notificationDate ? new Date(warning.notificationDate) : new Date();
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthYear]) {
      acc[monthYear] = {
        month: date.toLocaleString('default', { month: 'short', year: '2-digit' }),
        count: 0,
        avgScore: 0,
        totalScore: 0
      };
    }
    acc[monthYear].count += 1;
    acc[monthYear].totalScore += warning.score || 0;
    return acc;
  }, {});

  const trendData = Object.values(warningsByMonth).map(item => ({
    ...item,
    avgScore: item.count > 0 ? Math.round((item.totalScore / item.count) * 10) / 10 : 0
  })).sort((a, b) => a.month.localeCompare(b.month));

  return (
    <>
      <ContentSection title="Risk Analysis">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Risk Matrix</h3>
            <div className="grid grid-cols-6 gap-1 mb-2">
              <div className="text-right text-xs flex items-center justify-end pr-2 font-medium">Likelihood</div>
              {[5, 4, 3, 2, 1].map(likelihood => (
                <div key={`likelihood-row-${likelihood}`} className="contents">
                  <div className="h-10 flex items-center justify-center text-xs font-medium">
                    {likelihood}
                  </div>
                  {[1, 2, 3, 4, 5].map(severity => {
                    const cell = riskMatrixData.find(d => 
                      d.severity === severity && d.likelihood === likelihood
                    );
                    return (
                      <div 
                        key={`cell-${likelihood}-${severity}`}
                        className="h-10 border rounded-md flex items-center justify-center relative cursor-pointer transition hover:opacity-90"
                        style={{ backgroundColor: cell?.color }}
                        title={`Likelihood: ${likelihood}, Severity: ${severity}, Risk Score: ${cell?.score}, Count: ${cell?.count}`}
                      >
                        {cell?.count > 0 && (
                          <div className="text-xs font-medium">{cell.count}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div key="spacer-1"></div>
              {[1, 2, 3, 4, 5].map(severity => (
                <div key={`severity-col-${severity}`} className="h-8 flex items-center justify-center text-xs font-medium">
                  {severity}
                </div>
              ))}
              <div key="spacer-2"></div>
              <div key="severity-label" className="col-span-5 text-center text-xs font-medium">Severity</div>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {[
                { color: 'bg-gray-100', label: 'Low (1-5)' },
                { color: 'bg-yellow-50', label: 'Medium (6-9)' },
                { color: 'bg-amber-100', label: 'High (10-14)' },
                { color: 'bg-red-100', label: 'Critical (15+)' },
              ].map((item, index) => (
                <div key={`legend-${index}`} className="flex items-center">
                  <div className={`w-3 h-3 ${item.color} mr-1`}></div>
                  <span className="text-xs">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Warning Status Distribution</h3>
            <div className="flex items-center">
              <div className="w-1/2 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={statusColors[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} warnings`, 'Count']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2">
                <div className="space-y-3">
                  {statusData.map((status) => (
                    <div key={`status-${status.name}`} className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: statusColors[status.name] }}
                      ></div>
                      <div className="text-sm">{status.name}</div>
                      <div className="ml-auto text-sm font-medium">{status.value}</div>
                    </div>
                  ))}
                  <div className="pt-2 border-t">
                    <div className="flex justify-between">
                      <div className="text-sm font-medium">Total:</div>
                      <div className="text-sm font-medium">{warningData.length}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection title="Impact Analysis">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Warning Impact Types</h3>
            <div className="space-y-4">
              {impactData.map(impact => (
                <div key={`impact-${impact.name}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{impact.name}</span>
                    <span>{impact.count} ({impact.percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${impact.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Status Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(statusCounts).map(([status, count]) => {
                const percentage = warningData.length > 0 ? Math.round((count / warningData.length) * 100) : 0;
                let bgColor = 'bg-blue-100';
                let textColor = 'text-blue-800';
                if (status === 'OVERDUE') {
                  bgColor = 'bg-red-100';
                  textColor = 'text-red-800';
                } else if (status === 'AVOIDED') {
                  bgColor = 'bg-green-100';
                  textColor = 'text-green-800';
                }
                return (
                  <div key={`summary-${status}`} className={`${bgColor} ${textColor} rounded-lg p-3 text-center`}>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-xs font-medium">{status.toLowerCase()}</div>
                    <div className="text-xs mt-1">({percentage}%)</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6">
              <h4 className="text-xs font-medium mb-2">Risk Score Distribution</h4>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'Low (1-5)', count: warningData.filter(w => w.score <= 5).length },
                    { name: 'Medium (6-9)', count: warningData.filter(w => w.score > 5 && w.score <= 9).length },
                    { name: 'High (10-14)', count: warningData.filter(w => w.score > 9 && w.score <= 14).length },
                    { name: 'Critical (15+)', count: warningData.filter(w => w.score >= 15).length },
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>
      
      <ContentSection title="Trend Analysis">
        <div className="rounded-lg border bg-white p-4 h-80">
          <h3 className="text-sm font-medium mb-4">Warning Trends Over Time</h3>
          <ResponsiveContainer width="100%" height="85%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="count" stroke="#3B82F6" name="Number of Warnings" />
              <Line yAxisId="right" type="monotone" dataKey="avgScore" stroke="#EF4444" name="Avg. Risk Score" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ContentSection>
    </>
  );
};

const MainContent = memo(
  ({
    isCollapsed,
    projectName,
    setProjectName,
    clientName,
    setClientName,
    projectStartDate,
    setProjectStartDate,
    projectEndDate,
    setProjectEndDate,
    onAddWarning,
    handleSort,
    sortField,
    sortDirection,
    filteredData,
    handleEditWarning,
    downloadCSV,
    statusFilter,
    setStatusFilter,
    warningData,
  }: MainContentProps) => (
    <main
      className={cn(
        'flex-1 overflow-y-auto transition-all duration-300',
        isCollapsed ? 'md:ml-20' : 'md:ml-80'
      )}
      role="main"
    >
      <Container className="px-2 sm:px-4">
        <section className="space-y-4 sm:space-y-8 py-4 sm:py-6">
          <ContentSection title="Project Details">
            <div className="rounded-xl border bg-card text-card-foreground shadow transition hover:shadow-lg p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="projectName" className="text-sm font-medium">
                    Project Name
                  </label>
                  <input
                    id="projectName"
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="Enter project name"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="clientName" className="text-sm font-medium">
                    Client Name
                  </label>
                  <input
                    id="clientName"
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    placeholder="Enter client name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Project Start Date</label>
                  <input
                    type="date"
                    value={projectStartDate}
                    onChange={(e) => setProjectStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Project End Date</label>
                  <input
                    type="date"
                    value={projectEndDate}
                    onChange={(e) => setProjectEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
              </div>
            </div>
          </ContentSection>

          <ContentSection title="Early Warnings Register">
            <div className="flex justify-end mb-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="overdue">Overdue</option>
                <option value="avoided">Avoided</option>
              </select>
            </div>
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={onAddWarning}
                className="flex items-center px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white font-medium focus:outline-none focus:ring-2 focus:ring-green-400 transition"
              >
                <Plus className="mr-2 h-4 w-4" /> Add Early Warning
              </button>
              <button
                onClick={downloadCSV}
                className="flex items-center px-3 py-1 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                Download CSV
              </button>
            </div>
            <div className="rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('id')}>
                      System ID {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('reference')}>
                      Reference {sortField === 'reference' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('role')}>
                      From {sortField === 'role' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('title')}>
                      Title {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('notificationDate')}>
                      Notification Date {sortField === 'notificationDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('increasePrices')}>
                      Price {sortField === 'increasePrices' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('delayCompletion')}>
                      Completion {sortField === 'delayCompletion' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('delayKeyDate')}>
                      Key Date {sortField === 'delayKeyDate' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('impairPerformance')}>
                      Performance {sortField === 'impairPerformance' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('increaseContractorCost')}>
                      Cost {sortField === 'increaseContractorCost' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('score')}>
                      Score {sortField === 'score' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th scope="col" className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition" onClick={() => handleSort('status')}>
                      Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length > 0 ? (
                    filteredData.map((warning) => (
                      <tr
                        key={warning.id}
                        className={warning.status === 'OVERDUE' ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}
                        onClick={() => handleEditWarning(warning)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="px-4 py-3 text-sm font-medium">{warning.id}</td>
                        <td className="px-4 py-3 text-sm">{warning.reference || '-'}</td>
                        <td className="px-4 py-3 text-sm">{warning.role}</td>
                        <td className="px-4 py-3 text-sm">{warning.title}</td>
                        <td className="px-4 py-3 text-sm">{warning.notificationDate}</td>
                        <td className="px-4 py-3 text-sm text-center">{warning.impacts?.increasePrices ? '✓' : ''}</td>
                        <td className="px-4 py-3 text-sm text-center">{warning.impacts?.delayCompletion ? '✓' : ''}</td>
                        <td className="px-4 py-3 text-sm text-center">{warning.impacts?.delayKeyDate ? '✓' : ''}</td>
                        <td className="px-4 py-3 text-sm text-center">{warning.impacts?.impairPerformance ? '✓' : ''}</td>
                        <td className="px-4 py-3 text-sm text-center">{warning.impacts?.increaseContractorCost ? '✓' : ''}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            warning.score >= 4 ? 'bg-red-100 text-red-800' :
                            warning.score >= 3 ? 'bg-amber-100 text-amber-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {warning.score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            warning.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                            warning.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {warning.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                        No early warnings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="flex justify-between items-center p-4 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  {filteredData.length > 0
                    ? `Showing ${filteredData.length} early warning${filteredData.length !== 1 ? 's' : ''}`
                    : 'No early warnings'}
                </div>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1 text-sm rounded-md border border-gray-300 bg-white text-gray-400" disabled>
                    Previous
                  </button>
                  <button className="px-3 py-1 text-sm rounded-md border border-gray-300 bg-white text-gray-400" disabled>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </ContentSection>

          <DataVisualizations filteredData={filteredData} warningData={warningData} />
        </section>
      </Container>
    </main>
  )
);

export function EarlyWarningRegister() {
  const { isCollapsed } = useSidebar();
  const { state, dispatch } = useEarlyWarning();

  const [projectName, setProjectName] = useState('');
  const [clientName, setClientName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState('');
  const [projectEndDate, setProjectEndDate] = useState('');
  const [openWarningForm, setOpenWarningForm] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'notes' | 'actions'>('details');
  const [selectedWarning, setSelectedWarning] = useState<any | null>(null);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [warningData, setWarningData] = useState<any[]>([]);
  const [severity, setSeverity] = useState(1);
  const [likelihood, setLikelihood] = useState(1);
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState<any[]>([]);

  const currentData: EarlyWarningData = {
    projectName,
    clientName,
    projectStartDate,
    projectEndDate,
    warnings: warningData,
  };

  useEffect(() => {
    console.log("Current data for saving:", currentData);
    dispatch({
      type: 'UPDATE_DATA',
      payload: currentData
    });
  }, [projectName, clientName, projectStartDate, projectEndDate, warningData, dispatch]);

  const riskScore = severity * likelihood;

  const getFieldValue = (warning: any, field: string) => {
    const impactFields = [
      'increasePrices',
      'delayCompletion',
      'delayKeyDate',
      'impairPerformance',
      'increaseContractorCost',
    ];
    if (impactFields.includes(field)) {
      return warning.impacts ? warning.impacts[field] : false;
    }
    return warning[field];
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const newNote = {
      id: Date.now(),
      text: noteText,
      author: 'Current User',
      date: new Date().toISOString(),
    };
    if (selectedWarning) {
      const updatedWarning = {
        ...selectedWarning,
        notes: [...(selectedWarning.notes || []), newNote],
      };
      setWarningData(
        warningData.map((w) => (w.id === selectedWarning.id ? updatedWarning : w))
      );
      setSelectedWarning(updatedWarning);
    } else {
      setNotes([...notes, newNote]);
    }
    setNoteText('');
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedData = [...warningData].sort((a, b) => {
    const aVal = getFieldValue(a, sortField);
    const bVal = getFieldValue(b, sortField);
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredData = sortedData.filter((warning) =>
    statusFilter === 'all' ? true : warning.status.toLowerCase() === statusFilter
  );

  const handleAddWarning = () => {
    setSelectedWarning(null);
    setActiveTab('details');
    setSeverity(1);
    setLikelihood(1);
    setNotes([]);
    setOpenWarningForm(true);
  };

  const handleEditWarning = (warning: any) => {
    setSelectedWarning(warning);
    setSeverity(warning.severity || 1);
    setLikelihood(warning.likelihood || 1);
    setActiveTab('details');
    setOpenWarningForm(true);
  };

  const handleSaveWarning = () => {
    const title = (document.getElementById('title') as HTMLInputElement)?.value;
    const reference = (document.getElementById('reference') as HTMLInputElement)?.value;
    const description = (document.getElementById('description') as HTMLTextAreaElement)?.value;
    const notifiedBy = (document.getElementById('notified-by') as HTMLInputElement)?.value;
    const email = (document.getElementById('email') as HTMLInputElement)?.value;
    const notificationDate = (document.getElementById('notification-date') as HTMLInputElement)?.value;
    const replyDue = (document.getElementById('reply-due') as HTMLInputElement)?.value;
    const meetingDate = (document.getElementById('meeting-date') as HTMLInputElement)?.value;
    const actionsTaken = (document.getElementById('actions-taken') as HTMLTextAreaElement)?.value;

    const increasePrices = (document.getElementById('impact1') as HTMLInputElement)?.checked;
    const delayCompletion = (document.getElementById('impact2') as HTMLInputElement)?.checked;
    const delayKeyDate = (document.getElementById('impact3') as HTMLInputElement)?.checked;
    const impairPerformance = (document.getElementById('impact4') as HTMLInputElement)?.checked;
    const increaseContractorCost = (document.getElementById('impact5') as HTMLInputElement)?.checked;

    const riskStatusAvoided = (document.getElementById('avoided') as HTMLInputElement)?.checked;
    const replyDueDate = new Date(replyDue);
    const currentDate = new Date();

    let status = 'ACTIVE';
    if (riskStatusAvoided) {
      status = 'AVOIDED';
    } else if (replyDueDate < currentDate) {
      status = 'OVERDUE';
    }

    const newWarning = {
      id: selectedWarning ? selectedWarning.id : `EW-${warningData.length + 1}`,
      reference,
      role: notifiedBy,
      title,
      description,
      replyDue,
      replyDate: '',
      email,
      notificationDate,
      severity,
      likelihood,
      score: riskScore,
      status,
      impacts: {
        increasePrices,
        delayCompletion,
        delayKeyDate,
        impairPerformance,
        increaseContractorCost,
      },
      riskStatus: riskStatusAvoided ? 'Avoided' : 'Present',
      notes: selectedWarning ? selectedWarning.notes || [] : notes,
      meetingDate,
      actionsTaken,
    };

    if (selectedWarning) {
      setWarningData(
        warningData.map((w) => (w.id === selectedWarning.id ? newWarning : w))
      );
    } else {
      setWarningData([...warningData, newWarning]);
    }
    setOpenWarningForm(false);
  };

  const downloadCSV = () => {
    if (!warningData.length) return;
    const headers = [
      'ID',
      'Reference',
      'From',
      'Title',
      'Notification Date', // Updated from 'Reply Due'
      'Price',
      'Completion',
      'Key Date',
      'Performance',
      'Cost',
      'Score',
      'Status',
    ];
    const rows = filteredData.map((warning) => [
      warning.id,
      warning.reference || '',
      warning.role,
      warning.title,
      warning.notificationDate, // Updated from warning.replyDue
      warning.impacts?.increasePrices ? '✓' : '',
      warning.impacts?.delayCompletion ? '✓' : '',
      warning.impacts?.delayKeyDate ? '✓' : '',
      warning.impacts?.impairPerformance ? '✓' : '',
      warning.impacts?.increaseContractorCost ? '✓' : '',
      warning.score,
      warning.status,
    ]);
    let csvContent = headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\n';
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'early_warnings_register.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLoad = useCallback(
    (calculation: any) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: calculation.data
      });
      if (calculation.data) {
        const data = calculation.data;
        if (data.projectName) setProjectName(data.projectName);
        if (data.clientName) setClientName(data.clientName);
        if (data.projectStartDate) setProjectStartDate(data.projectStartDate);
        if (data.projectEndDate) setProjectEndDate(data.projectEndDate);
        if (data.warnings && Array.isArray(data.warnings)) setWarningData(data.warnings);
      }
    },
    [dispatch]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MemoizedPrimaryHeader className="px-2 sm:px-4" />
      <MemoizedEarlyWarningHeader className="px-2 sm:px-4" />
      <div className="flex flex-1 flex-col md:flex-row">
        <EarlyWarningSidebar
          data={currentData}
          onLoad={handleLoad}
          className={cn(
            'w-full overflow-y-auto border-b transition-all duration-300',
            'md:border-r md:border-b-0',
            isCollapsed ? 'md:w-20' : 'md:w-80'
          )}
        />
        <MainContent
          isCollapsed={isCollapsed}
          projectName={projectName}
          setProjectName={setProjectName}
          clientName={clientName}
          setClientName={setClientName}
          projectStartDate={projectStartDate}
          setProjectStartDate={setProjectStartDate}
          projectEndDate={projectEndDate}
          setProjectEndDate={setProjectEndDate}
          onAddWarning={handleAddWarning}
          handleSort={handleSort}
          sortField={sortField}
          sortDirection={sortDirection}
          filteredData={filteredData}
          handleEditWarning={handleEditWarning}
          downloadCSV={downloadCSV}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          warningData={warningData}
        />
      </div>
      {openWarningForm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-opacity"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {selectedWarning ? 'Edit Early Warning' : 'New Early Warning'}
                </h2>
                <button
                  onClick={() => setOpenWarningForm(false)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  aria-label="Close modal"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="border-b border-gray-200 mb-6">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      activeTab === 'details'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => setActiveTab('notes')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      activeTab === 'notes'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Notes
                  </button>
                  <button
                    onClick={() => setActiveTab('actions')}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      activeTab === 'actions'
                        ? 'border-b-2 border-blue-600 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Actions
                  </button>
                </div>
              </div>
              <div className="min-h-[60vh]">
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium mb-1">
                        Title
                      </label>
                      <input
                        id="title"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.title : ""}
                      />
                    </div>
                    <div>
                      <label htmlFor="reference" className="block text-sm font-medium mb-1">
                        Reference
                      </label>
                      <input
                        id="reference"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.reference : ""}
                      />
                    </div>
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.description : ""}
                      />
                    </div>
                    <div>
                      <label htmlFor="notified-by" className="block text-sm font-medium mb-1">
                        Notified By
                      </label>
                      <input
                        id="notified-by"
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.role : ""}
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-1">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.email : ""}
                      />
                    </div>
                    <div>
                      <label htmlFor="notification-date" className="block text-sm font-medium mb-1">
                        Notification Date
                      </label>
                      <input
                        id="notification-date"
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.notificationDate : new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div>
                      <label htmlFor="reply-due" className="block text-sm font-medium mb-1">
                        Reply Due
                      </label>
                      <input
                        id="reply-due"
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.replyDue : (() => {
                          const date = new Date();
                          date.setDate(date.getDate() + 14);
                          return date.toISOString().split('T')[0];
                        })()}
                      />
                    </div>
                    <div>
                      <label htmlFor="meeting-date" className="block text-sm font-medium mb-1">
                        Meeting Date
                      </label>
                      <input
                        id="meeting-date"
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        defaultValue={selectedWarning ? selectedWarning.meetingDate : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Impacts</h3>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="impact1"
                          defaultChecked={selectedWarning?.impacts?.increasePrices || false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Increase Prices</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="impact2"
                          defaultChecked={selectedWarning?.impacts?.delayCompletion || false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Delay Completion</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="impact3"
                          defaultChecked={selectedWarning?.impacts?.delayKeyDate || false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Delay Key Date</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="impact4"
                          defaultChecked={selectedWarning?.impacts?.impairPerformance || false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Impair Performance</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="impact5"
                          defaultChecked={selectedWarning?.impacts?.increaseContractorCost || false}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Increase Contractor Cost</span>
                      </label>
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-sm font-medium">Risk Status</h3>
                      <div className="flex space-x-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="risk-status"
                            id="present"
                            defaultChecked={!selectedWarning || selectedWarning.riskStatus !== 'Avoided'}
                            className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">Risk Still Present</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="risk-status"
                            id="avoided"
                            defaultChecked={selectedWarning?.riskStatus === 'Avoided'}
                            className="rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">Risk Avoided / Passed</span>
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="severity" className="block text-sm font-medium mb-1">
                          Severity
                        </label>
                        <select
                          id="severity"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          value={severity}
                          onChange={(e) => setSeverity(parseInt(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5].map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="likelihood" className="block text-sm font-medium mb-1">
                          Likelihood
                        </label>
                        <select
                          id="likelihood"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          value={likelihood}
                          onChange={(e) => setLikelihood(parseInt(e.target.value))}
                        >
                          {[1, 2, 3, 4, 5].map((val) => (
                            <option key={val} value={val}>
                              {val}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                      <button
                        onClick={() => setOpenWarningForm(false)}
                        className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveWarning}
                        className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Save Early Warning
                      </button>
                    </div>
                  </div>
                )}
                {activeTab === 'notes' && (
                  <div>
                    <div className="space-y-4 mb-6 max-h-[50vh] overflow-y-auto p-4 border border-gray-200 rounded-lg">
                      {selectedWarning && selectedWarning.notes && selectedWarning.notes.length > 0 ? (
                        selectedWarning.notes.map((note: any) => (
                          <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">{note.author}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(note.date).toLocaleString()}
                              </div>
                            </div>
                            <div className="text-sm">{note.text}</div>
                          </div>
                        ))
                      ) : notes.length > 0 ? (
                        notes.map((note: any) => (
                          <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">{note.author}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(note.date).toLocaleString()}
                              </div>
                            </div>
                            <div className="text-sm">{note.text}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          No notes have been added yet
                        </div>
                      )}
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                      <label htmlFor="note-text" className="block text-sm font-medium mb-2">
                        Add Note
                      </label>
                      <div className="flex gap-2">
                        <textarea
                          id="note-text"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          rows={3}
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          placeholder="Add a note about this early warning..."
                        />
                      </div>
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleAddNote}
                          disabled={!noteText.trim()}
                          className={`px-4 py-2 text-sm font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            noteText.trim()
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Add Note
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end gap-2">
                      <button
                        onClick={() => setOpenWarningForm(false)}
                        className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Close
                      </button>
                      <button
                        onClick={handleSaveWarning}
                        className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Save Early Warning
                      </button>
                    </div>
                  </div>
                )}
                {activeTab === 'actions' && (
                  <div>
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="actions-taken" className="block text-sm font-medium mb-1">
                          Actions Taken
                        </label>
                        <textarea
                          id="actions-taken"
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                          defaultValue={selectedWarning ? selectedWarning.actionsTaken : ""}
                          placeholder="Describe the actions taken for this early warning..."
                        />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end gap-2">
                      <button
                        onClick={() => setOpenWarningForm(false)}
                        className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Close
                      </button>
                      <button
                        onClick={handleSaveWarning}
                        className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                      >
                        Save Early Warning
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}